<template>
  <flow-layout :current-step-index="isBrokerFlow ? 4 : 3">
    <heading level="2">{{ isBrokerFlow ? $t('ownerInfo.brokerTitle') : $t('ownerInfo.title') }}</heading>

    <form autocomplete="off">
      <address-autocomplete
        id="home-address"
        labelPathReference="ownerInfo"
        v-model="primaryOwner"
        @input="checkFormValidation"
        validationPathPrefix="/owners/*"
        :maxlength="ADDRESS_MAX_LENGTH"
        :postalCodePlaceholder="$t('postalCodePlaceholder')"
        :placeholder="'ownerInfo.addressPlaceholder'"
        :stateDefaultValue="$t('select')"
        @autocomplete="onPlaceChange"
      />

      <input-inline>
        <input-text-masked
          id="additional-phone"
          ref="additionalPhone"
          class="additionalPhone"
          @input="checkFormValidation"
          name="additional-phone"
          v-model="primaryOwner.additionalPhone"
          :maxlength="PHONE_MAX_LENGTH"
          v-validation.ifPresent="[['dynamic', '/owners/*/additionalPhone']]"
          :mask="phoneMask"
          :cleanup-pattern="/[-|\s|(|)]/g"
          :placeholder-char="'\u2000'"
          :guide="false"
        >
          <template #label>{{ $t('ownerInfo.additionalPhoneField.label') }}</template>
        </input-text-masked>
      </input-inline>

      <input-inline>
        <input-age
          id="date-of-birth"
          ref="dateOfBirth"
          @input="checkFormValidation"
          v-model="primaryOwner.dateOfBirth"
          v-validation="[['dynamic', '/owners/*/dateOfBirth']]"
        >
          <template #label>{{ $t('ownerInfo.dateOfBirthLabel') }}</template>
        </input-age>
      </input-inline>

      <input-inline>
        <input-select
          id="residency-status"
          name="residency-status"
          class="residency"
          @change="checkFormValidation"
          v-model="primaryOwner._extension.residencyStatus"
          :options="residencyStatusOptions"
          v-validation:closed="[['dynamic', '/owners/*/_extension/residencyStatus']]"
          :defaultValue="$t('select')"
        >
          <template #label>{{
            isBrokerFlow
              ? $t('ownerInfo.residencyStatus.brokerLabel')
              : $t('ownerInfo.residencyStatus.label')
          }}</template>
        </input-select>
      </input-inline>

      <input-inline>
        <input-select
          id="how-did-you-hear-about-us"
          name="how-did-you-hear-about-us"
          class="hearAboutUs"
          @change="checkFormValidation"
          v-model="application.hearAboutUs"
          :options="howDidYouHearAboutUsOptions"
          v-validation:closed="[['dynamic', '/hearAboutUs']]"
          :defaultValue="$t('select')"
        >
          <template #label>{{ $t('ownerInfo.howDidYouHearAboutUs.label') }}</template>
        </input-select>
      </input-inline>

      <input-inline>
        <input-text
          type="text"
          @input="checkFormValidation"
          id="unique-loan-code"
          name="unique-loan-code"
          :maxlength="UNIQUE_LOAN_CODE_LENGTH"
          :strict-mode="{ regex: /[^0-9a-zA-Z]/g, skipDefault: true }"
          v-model="application._extension.uniqueLoanCode"
          v-validation="[['dynamic', '/_extension/uniqueLoanCode']]"
        >
          <template #label>{{ $t('ownerInfo.uniqueLoanCodeLabel') }}</template>
          <template #description>{{ $t('ownerInfo.uniqueLoanCodeLabelSuffix') }}</template>
        </input-text>
      </input-inline>

      <navigation-buttons
        :next="{
          label: $t('nextButtonLabel'),
          modifiers: { disabled: !isFormValid },
        }"
        @next="submit"
        @back="goBack"
      />
    </form>
  </flow-layout>
</template>
<script>
import FlowLayout from '@/views/layouts/FlowLayout';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import InputInline from '@/components/InputInline';
import InputAge from '@/components/InputAge';
import {
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  UNIQUE_LOAN_CODE_LENGTH,
} from '@/modules/validation-service/fields-length';
import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import states from '@/modules/states';
import residencyStatusTypes from '@/modules/residency-status-types';
import informationSourceTypes from '@/modules/information-source-types';
import { getPhoneMask } from '@/modules/field-mask';

export default {
  name: 'personal-info',
  components: {
    FlowLayout,
    InputInline,
    InputAge,
    AddressAutocomplete,
  },
  data() {
    return {
      ADDRESS_MAX_LENGTH,
      CITY_MAX_LENGTH,
      POSTAL_CODE_MAX_LENGTH,
      PHONE_MAX_LENGTH,
      UNIQUE_LOAN_CODE_LENGTH,
      isFormValid: false,
    };
  },
  computed: {
    primaryOwner: {
      get() {
        return this.application.owners[0];
      },
      set(value) {
        this.$set(this.application.owners, 0, value);
      },
    },
    states() {
      return mapConstsToOptions(states, 'states');
    },
    residencyStatusOptions() {
      return mapConstsToOptions(residencyStatusTypes, 'ownerInfo.residencyStatus.options');
    },
    howDidYouHearAboutUsOptions() {
      return mapConstsToOptions(informationSourceTypes, 'ownerInfo.howDidYouHearAboutUs.options');
    },
    phoneMask() {
      return getPhoneMask(this.primaryOwner.additionalPhone);
    },
  },
  methods: {
    checkFormValidation() {
      this.$nextTick().then(() => {
        this.isFormValidFn();
      });
    },
    isFormValidFn() {
      let validationCounter = 0;
      this.isFormValid = false;
      this.$validations.collections[0].forEach((value, key) => {
        if (
          key === 'additional-phone' ||
          key === 'unique-loan-code' ||
          key === 'home-address-unit-number'
        ) {
          validationCounter += 1;
        } else if (key === 'date-of-birth' && value.component.value) {
          validationCounter += 1;
        } else if (
          value.component.value &&
          (value.component.errorMessage === '' || value.component.errorMessage === undefined)
        ) {
          validationCounter += 1;
        } else if (
          value.component.empty === false &&
          (value.component.errorMessage === '' || value.component.errorMessage === undefined)
        ) {
          validationCounter += 1;
        }
      });
      this.isFormValid = validationCounter === this.$validations.collections[0].size;
    },
    setFocusTo(element) {
      if (element) {
        this.$nextTick().then(() => {
          element.focus();
        });
      }
    },
    onPlaceChange() {
      this.setFocusTo(this.$refs.additionalPhone.$refs.maskedInput.$refs.input);
    },
  },
  mixins: [applicationMixin, brokerFlowMixin],
};
</script>
<style lang="sass" scoped></style>
